import { useState, useEffect } from "react";
import { Music, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface FileItem {
  _id: string;
  mimeType: string;
  originalname: string;
  size: number;
  duration?: number;
  url?: string;
}

interface AudioSelectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (file: { fileId: string; url: string; originalname: string }) => void;
  title?: string;
  description?: string;
}

function formatSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function AudioSelectDialog({
  open,
  onOpenChange,
  onSelect,
  title = "Select Audio",
  description = "Choose an audio file from your uploaded files or upload a new one",
}: AudioSelectDialogProps) {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileInput, setUploadFileInput] = useState<HTMLInputElement | null>(null);

  const fetchFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const res = await fetch("/api/files?mimeType=audio");
      const data = await res.json();
      setFiles(data.files || []);
    } catch (error) {
      console.error("Failed to fetch files:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchFiles();
    }
  }, [open]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      alert("Please select an audio file");
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/files", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to upload file");
      }

      const data = await res.json();

      if (data.file?._id && data.file?.url) {
        onSelect({
          fileId: data.file._id,
          url: data.file.url,
          originalname: data.file.originalname || file.name,
        });
        onOpenChange(false);
      }

      await fetchFiles();

      if (uploadFileInput) {
        uploadFileInput.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert(error instanceof Error ? error.message : "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectFile = (file: FileItem) => {
    if (file.url) {
      onSelect({
        fileId: file._id,
        url: file.url,
        originalname: file.originalname,
      });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="mb-4">
          <input
            ref={setUploadFileInput}
            type="file"
            accept="audio/*"
            onChange={handleFileUpload}
            className="hidden"
            id="audio-select-upload"
            disabled={isUploading}
          />
          <label
            htmlFor="audio-select-upload"
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                Uploading...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Upload Audio
              </>
            )}
          </label>
        </div>
        {isLoadingFiles ? (
          <div className="flex items-center justify-center py-4">
            <div className="text-gray-500">Loading files...</div>
          </div>
        ) : files.length === 0 ? (
          <div className="flex items-center justify-center py-4">
            <div className="text-gray-500">No audio files found</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 py-4">
            {files.map((file) => (
              <div
                key={file._id}
                className="border rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 transition-colors p-4 flex items-start gap-4"
                onClick={() => handleSelectFile(file)}
              >
                <div className="shrink-0 flex items-center justify-center size-10 rounded-md bg-muted">
                  <Music className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div>
                    <div className="text-sm font-medium truncate">{file.originalname}</div>
                    <div className="text-xs text-gray-500">{formatSize(file.size)}</div>
                  </div>
                  {file.url && (
                    <audio
                      controls
                      src={file.url}
                      className="w-full h-8"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
